import { Injectable, inject } from '@angular/core';
import { Workbook, Worksheet } from 'exceljs';
import jsPDF from 'jspdf';
import autoTable, { Styles } from 'jspdf-autotable';
import { reportConstants } from '../../common/constants';
import { reportConstantsMessage } from '../../common/constants';
import { SeparatorGasCompositionComponentModel, SeparatorGasReports } from '../../models/project';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFSeparatorGasRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);
    private commonStylesWithoutBorder = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    GenerateSeparatorGasPdfReport(doc: jsPDF, marginSize: number, separatorData: SeparatorGasReports): void {
        if (separatorData !== undefined && separatorData !== null) {
            doc.addPage();
            let tableStartY = marginSize - 5;
            marginSize += 5;
            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.gasComposition, marginSize + 30, tableStartY);
            tableStartY += 5;
            if (separatorData.subTitle) {
                doc.text(separatorData.subTitle, marginSize + 30, tableStartY);
                tableStartY += 6;
                doc.setFontSize(8);
                doc.setFont(reportConstants.eMprintCondensedBold);
            }
            const setMargin = marginSize + 30;
            autoTable(doc, {
                head: [
                    [
                        { content: reportConstants.component, colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercentage, styles: { halign: reportConstants.center } }
                    ]
                ],
                body: separatorData.data.map((val: SeparatorGasCompositionComponentModel) => [val.component, val.componentName, Number(val.componentMolePercentage).toFixed(3)]),
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: setMargin, right: setMargin },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: {
                    fontSize: 7,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedBold,
                    cellPadding: { top: 0.5, bottom: 0.5 }
                },
                bodyStyles: {
                    fontSize: 7,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.2, bottom: 0.2 },
                    lineWidth: 0
                },
                columnStyles: {
                    0: { cellWidth: 15 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 15 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && (data.column.index === 0 || data.column.index === 1)) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {

                    const lw = reportConstants.tableLinewidth;

                    const { cell, column, doc, section, table } = data;

                    doc.setDrawColor(0, 0, 0);
                    doc.setLineWidth(lw);

                    const x = cell.x;
                    const y = cell.y;
                    const w = cell.width;
                    const h = cell.height;

                    doc.line(x, y, x, y + h);

                    if (column.index === table.columns.length - 1) {
                        doc.line(x + w, y, x + w, y + h);
                    }

                    doc.line(x, y + h, x + w, y + h);

                    if (section === reportConstants.head) {
                        doc.line(x, y, x + w, y);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 5;
        }
    }

    async GenerateSeparatorGasExcelReport(workbook: Workbook, worksheetName: string, separatorData: SeparatorGasReports, subHeader: string) {
        try {
            const worksheet = workbook.addWorksheet(worksheetName, {
                views: [{ showGridLines: false }]
            });
            const sepGasWorksheet = worksheet;

           this._reportGeneralUtilityService.AddSubHeaderToWorksheet(sepGasWorksheet, subHeader, reportConstants.c1, reportConstants.i1);

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
            sepGasWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            // Set default styles and column widths
            sepGasWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(sepGasWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0,//page 1
                fitToHeight: 0, // 1 page , 0 automatic
                scale: 80,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            sepGasWorksheet.getColumn(reportConstants.b).width = 5;
            sepGasWorksheet.getColumn(reportConstants.c).width = 10;
            sepGasWorksheet.getColumn(reportConstants.d).width = 18;
            sepGasWorksheet.getColumn(reportConstants.e).width = 10;
            sepGasWorksheet.getColumn(reportConstants.fChar).width = 5;

            // Title
            const titleCell = sepGasWorksheet.getCell(reportConstants.a3);
            titleCell.value = reportConstants.gasComposition;
            titleCell.font = { name: reportConstants.eMprintCondensedBold, size: 12, bold: true };
            titleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            let rowNo = 5;

            const SubtitleCell = sepGasWorksheet.getCell(reportConstants.a4);
            SubtitleCell.value = separatorData.subTitle;
            SubtitleCell.font = { name: reportConstants.eMprintCondensedBold, size: 10, bold: true };
            SubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            // Table Header
            sepGasWorksheet.mergeCells(`C${rowNo}:D${rowNo}`);
            const headerRow = sepGasWorksheet.getRow(rowNo++);
            const headers = [reportConstants.component, reportConstants.component, reportConstants.molePercentage];
            const headerCols = [3, 4, 5];

            headers.forEach((text, idx) => {
                const col = headerCols[idx];
                const cell = headerRow.getCell(col);
                cell.value = text;
                cell.font = { name: reportConstants.eMprintCondensedBold, size: 12, bold: true };
                cell.alignment = { horizontal: reportConstants.center };
                cell.border = {
                    top: { style: reportConstants.thin },
                    bottom: { style: reportConstants.double },
                    left: { style: reportConstants.thin },
                    right: { style: reportConstants.thin }
                };
            });
            headerRow.commit();

            // Table Body
            separatorData.data.forEach((val: SeparatorGasCompositionComponentModel) => {
                const row = sepGasWorksheet.getRow(rowNo++);
                row.getCell(3).value = val.component;
                row.getCell(4).value = val.componentName;
                row.getCell(5).value = Number(val.componentMolePercentage).toFixed(3);

                [3, 4].forEach((col) => {
                    const cell = row.getCell(col);
                    cell.font = { name: reportConstants.eMprintCondensedBold, size: 12, bold: true };
                    cell.alignment = { horizontal: reportConstants.center };
                    cell.border = {
                        top: { style: reportConstants.thin },
                        bottom: { style: reportConstants.thin },
                        left: { style: reportConstants.thin },
                        right: { style: reportConstants.thin }
                    };
                });

                [5].forEach((col) => {
                    const cell = row.getCell(col);
                    cell.font = { name: reportConstants.eMprintCondensedRegular, size: 12 };
                    cell.alignment = { horizontal: reportConstants.center };
                    cell.border = {
                        top: { style: reportConstants.thin },
                        bottom: { style: reportConstants.thin },
                        left: { style: reportConstants.thin },
                        right: { style: reportConstants.thin }
                    };
                });

                row.commit();
            });
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSeparatorReport, error);
        }
    }

    public AddOuterBorder(
            worksheet: Worksheet,
            startRowNo: number,
            endRowNo: number,
            startColumnNo: number,
            endColumnNo: number
        ) {
            for (let r = startRowNo; r <= endRowNo; r++) {
                for (let c = startColumnNo; c <= endColumnNo; c++) {
                    const cell = worksheet.getCell(r, c);
                    const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                    const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                    const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                    const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;
    
                    cell.border = {
                        top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
                        bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
                        left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                        right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
                    };
                }
            }
        }
}